import type {
	Verdict,
	VerdictSuggestionMethod,
	Verification,
	VerificationVideo,
} from "@sitescene/types";

import {
	type InfiniteData,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";

import { apiClient, type ApiResponse } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import { queryKeys } from "@/api/query-keys";
import { useOrgSelection } from "@/context/use-org-selection";

export interface VerificationFilters {
	classId?: string;
	decision?: string;
	/** "true" restricts to completed rows with no verdict yet. */
	needsVerdict?: string;
	status?: string;
}

const PAGE_SIZE = 50;

/**
 * Verification rows of the selected organization, newest first, paged by
 * cursor. Disabled until an organization is selected (the org rides along as
 * a header the API client reads at request time).
 */
export const useVerifications = (filters: VerificationFilters) => {
	const { selected } = useOrgSelection();
	const orgCode = selected?.orgCode ?? null;
	const activeFilters = Object.fromEntries(
		Object.entries(filters).filter(([, value]) => Boolean(value)),
	) as Record<string, string>;

	return useInfiniteQuery<
		ApiResponse<Verification[]>,
		Error,
		InfiniteData<ApiResponse<Verification[]>>,
		readonly unknown[],
		string | undefined
	>({
		enabled: orgCode !== null,
		getNextPageParam: (lastPage) =>
			lastPage.hasMore ? lastPage.endCursor : undefined,
		initialPageParam: undefined,
		queryFn: async ({ pageParam }) => {
			const response = await apiClient.get<Verification[]>(
				ENDPOINTS.shadow.plausibility.verifications,
				{
					params: { ...activeFilters, after: pageParam, limit: PAGE_SIZE },
				},
			);
			return response;
		},
		queryKey: queryKeys.plausibility.verifications(
			orgCode ?? "",
			activeFilters,
		),
	});
};

export const useVerification = (verificationId: string | undefined) => {
	const { selected } = useOrgSelection();
	const orgCode = selected?.orgCode ?? null;

	return useQuery({
		enabled: orgCode !== null && Boolean(verificationId),
		queryFn: async () => {
			const response = await apiClient.get<Verification>(
				ENDPOINTS.shadow.plausibility.verification(verificationId ?? ""),
			);
			return response.data;
		},
		queryKey: queryKeys.plausibility.verification(
			orgCode ?? "",
			verificationId ?? "",
		),
	});
};

/**
 * A signed playback URL for the verification's clip. The URL is good for an
 * hour; refetching before then would only re-sign the same key, so it is
 * held for most of that window and dropped when the page is left.
 */
export const useVerificationVideo = (verificationId: string | undefined) => {
	const { selected } = useOrgSelection();
	const orgCode = selected?.orgCode ?? null;

	return useQuery({
		enabled: orgCode !== null && Boolean(verificationId),
		gcTime: 0,
		queryFn: async () => {
			const response = await apiClient.get<VerificationVideo>(
				ENDPOINTS.shadow.plausibility.verificationVideo(verificationId ?? ""),
			);
			return response.data;
		},
		queryKey: queryKeys.plausibility.verificationVideo(
			orgCode ?? "",
			verificationId ?? "",
		),
		retry: false,
		staleTime: 50 * 60 * 1000,
	});
};

export interface RecordVerdictInput {
	note?: string;
	/** ISO instant the reviewer opened the row, for time-to-verdict. */
	openedAt: string;
	/** What the classifier proposed, when the reviewer asked for a suggestion. */
	suggestedClassId?: string;
	suggestionMethod?: VerdictSuggestionMethod;
	trueClassId: string;
}

export interface VerdictSuggestion {
	alertable: boolean;
	classId: string;
	displayName?: string;
	method: VerdictSuggestionMethod;
}

/** Record what the clip really showed. Refreshes the row and the lists. */
export const useRecordVerdict = (verificationId: string) => {
	const queryClient = useQueryClient();
	const { selected } = useOrgSelection();
	const orgCode = selected?.orgCode ?? "";
	return useMutation({
		mutationFn: async (input: RecordVerdictInput) => {
			const response = await apiClient.post<Verdict>(
				ENDPOINTS.shadow.plausibility.verdict(verificationId),
				input,
			);
			return response.data;
		},
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: queryKeys.plausibility.verification(
						orgCode,
						verificationId,
					),
				}),
				queryClient.invalidateQueries({
					queryKey: ["shadow", "plausibility", "verifications", orgCode],
				}),
			]);
		},
	});
};

/**
 * Ask the classifier which class the reviewer's own words describe. A
 * suggestion only — nothing is written until the reviewer records the verdict,
 * so there is no cache to invalidate here.
 */
export const useSuggestVerdictClass = (verificationId: string) =>
	useMutation({
		mutationFn: async (text: string) => {
			const response = await apiClient.post<VerdictSuggestion>(
				ENDPOINTS.shadow.plausibility.verdictSuggest(verificationId),
				{ text },
			);
			return response.data;
		},
	});
